import * as React from 'react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Skeleton } from '@/components/ui/skeleton'
import { CLIENT_CARE_TEAM_CHIP_LIMIT } from '@/config/constants'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import type { PatientRecord } from '@/features/patients/types'
import { formatMonthYear } from '@/features/patients/utils/clientDisplay'
import type { CareTeamMember } from '@/features/patients/utils/clientHistory'
import { buildCareTeam } from '@/features/patients/utils/clientHistory'
import { cn } from '@/lib/utils'

function initials(name: string): string {
  // "Last, First" reads first-initial-first, like the client avatar.
  const [last, first] = name.includes(',')
    ? name.split(',').map((part) => part.trim())
    : [name.split(/\s+/).at(-1) ?? '', name.split(/\s+/)[0] ?? '']
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase()
}

function MemberChip({ member }: { member: CareTeamMember }) {
  const lastSeen = member.lastVisit ? formatMonthYear(member.lastVisit) : null
  const detail = [
    member.assigned ? 'assigned' : null,
    member.visitCount > 0 ? String(member.visitCount) : null,
    member.inactive ? 'inactive' : lastSeen,
  ]
    .filter(Boolean)
    .join(' · ')
  const title =
    member.visitCount > 0
      ? `${member.visitCount} visit${member.visitCount === 1 ? '' : 's'}${lastSeen ? `, last ${lastSeen}` : ''}`
      : undefined

  return (
    <span
      title={title}
      className={cn(
        'bg-neutral-subtle-bg border-neutral-subtle-border inline-flex min-w-0 items-center gap-2 rounded-full border py-0.5 pr-3 pl-0.5 text-[13px]',
        member.assigned && 'bg-primary-ui-bg border-primary-subtle-border',
        member.inactive && 'opacity-50'
      )}>
      <span
        className={cn(
          'grid size-[22px] shrink-0 place-items-center rounded-full text-[10px] font-medium',
          member.assigned
            ? 'bg-primary-hover-bg text-primary-low-contrast'
            : 'bg-neutral-hover-bg text-muted-foreground'
        )}>
        {initials(member.name)}
      </span>
      <span className="text-text-heading truncate">{member.name}</span>
      <span className="text-muted-foreground shrink-0 text-xs">{detail}</span>
    </span>
  )
}

const NO_VISITS: never[] = []

function memberKey(member: CareTeamMember): string {
  return member.providerId ?? `assigned:${member.name}`
}

/**
 * Who looks after the client: the assigned provider on a row of their own,
 * then everyone the client has visited, most recent first, with a visit count
 * and the month last seen. Providers unseen for a year are dimmed.
 */
export function ClientCareTeamCard({
  patient,
  history,
  now,
}: {
  patient: PatientRecord
  history: ClientHistory
  now: Date
}) {
  const { visits, providerNames, providers } = history
  // Visit history only becomes a care team once providers can be named.
  const useVisits = visits.allowed && providers.allowed
  const visitRows = useVisits ? visits.rows : NO_VISITS
  const assignedName = patient.provider_info?.name ?? null
  const [expanded, setExpanded] = React.useState(false)

  const team = React.useMemo(
    () => buildCareTeam(visitRows, providerNames, assignedName, now),
    [visitRows, providerNames, assignedName, now]
  )

  const isLoading = useVisits && (visits.isLoading || providers.isLoading)
  const problem = !useVisits
    ? null
    : visits.isError
      ? 'Could not load visit history'
      : providers.isError
        ? 'Could not load provider names'
        : null
  // Visits the viewer may see, but not the providers who ran them.
  const namesHidden = visits.allowed && !providers.allowed
  // Nothing to say: no assignment, and no history the viewer can see.
  if (!isLoading && team.length === 0 && !problem && !namesHidden) return null

  const assigned = team.filter((member) => member.assigned)
  const others = team.filter((member) => !member.assigned)
  const shown = expanded ? others : others.slice(0, CLIENT_CARE_TEAM_CHIP_LIMIT)
  const canExpand = others.length > CLIENT_CARE_TEAM_CHIP_LIMIT

  return (
    <SheetSectionCard
      title={
        <span className="flex items-center justify-between gap-3">
          <span>Care team</span>
          {canExpand ? (
            <button
              type="button"
              className="text-primary-low-contrast rounded-sm tracking-normal hover:underline focus-visible:ring-primary-solid focus-visible:ring-2 focus-visible:outline-none"
              onClick={() => setExpanded((value) => !value)}>
              {expanded ? 'Show fewer' : `View all ${others.length}`}
            </button>
          ) : null}
        </span>
      }
      body={
        <div className="flex flex-col gap-2">
          {assigned.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {assigned.map((member) => (
                <MemberChip key={memberKey(member)} member={member} />
              ))}
            </div>
          ) : null}
          {isLoading || problem || namesHidden || others.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {shown.map((member) => (
                <MemberChip key={memberKey(member)} member={member} />
              ))}
              {isLoading ? (
                <Skeleton className="h-7 w-40 rounded-full" />
              ) : null}
              {problem ? (
                <span className="text-muted-foreground text-xs">{problem}</span>
              ) : null}
              {namesHidden ? (
                <span className="text-muted-foreground text-xs">
                  Providers from visit history need access to provider data
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      }
    />
  )
}
